// One stored evaluation: classification, status, reasons grouped by kind, CMC candidates, open gates, rule trace.
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useNames } from '../lib/names.ts';
import type { EvaluationRow } from '../lib/types.ts';
import { Classification, DemoStamp, Enum, fmtValue, Msg, ResultTag, StatusBadge } from './ui.tsx';

const REASON_ORDER = [
  'fail',
  'open_gate',
  'missing_mandatory',
  'pending_evidence',
  'genealogy_incomplete',
  'route_undetermined',
  'not_evaluable_blocking',
  'warning',
];

export function VerdictView({ e, compact = false }: { e: EvaluationRow; compact?: boolean }) {
  const { t } = useTranslation();
  const names = useNames();
  const v = e.verdict;
  // the rule pack writes "unassigned" when nobody owns an item yet; say so in words
  const owner = (o: string) => (!o || o === 'unassigned' ? t('owner.unassigned') : o);
  return (
    <section className="verdict">
      <DemoStamp count={v.assumptions.length} />
      <div className="summary">
        <div>
          <span className="summary-label">{t('summary.status')}</span>
          <StatusBadge status={v.display_status} />
          {v.display_reason && (
            <span className="summary-sub">
              <Msg k={`reason.${v.display_reason}`} />
            </span>
          )}
        </div>
        <div className="summary-wide">
          <span className="summary-label">{t('summary.route')}</span>
          <Classification verdict={v} />
        </div>
        <div>
          <span className="summary-label">{t('col.verdict')}</span>
          <strong>
            <Enum ns="verdict" v={v.verdict} />
          </strong>
          <span className="summary-sub">
            {t('batch.mode')} {v.mode} · {v.evaluation_date}
          </span>
        </div>
      </div>
      {v.assumptions.length > 0 && (
        <details open={!compact}>
          <summary>{t('batch.assumptions')}</summary>
          <ul>
            {v.assumptions.map((a) => (
              <li key={a.key}>
                <code>{a.key}</code> {t('batch.assumption_closes')} <code>{a.closes_gate}</code>.{' '}
                {t('batch.assumption_status')}: {a.real_world_status} {t('batch.assumption_change')}:{' '}
                {a.what_would_change_it}
              </li>
            ))}
          </ul>
        </details>
      )}
      <h3>{t('batch.reasons')}</h3>
      {REASON_ORDER.map((code) => {
        const rs = v.reasons.filter((r) => r.code === code);
        if (rs.length === 0) return null;
        return (
          <div key={code} className={`reasons reasons-${code}`}>
            <h4>
              <Msg k={`reason.${code}`} /> ({rs.length})
            </h4>
            {code === 'fail' && v.display_status !== 'FAIL' && (
              <p className="small muted">{t('batch.fail_not_fail')}</p>
            )}
            <ul>
              {rs.map((r, i) => (
                <li key={`${r.rule_id ?? r.field_id ?? ''}-${i}`}>
                  <code>{r.rule_id ?? r.field_id}</code>{' '}
                  <strong>{r.rule_id ? names.rule(r.rule_id) : names.field(r.field_id ?? '')}</strong>
                  {': '}
                  <Msg k={r.message.key} /> · {t('col.owner')}: {owner(r.owner)}
                  {r.evidence_required && (
                    <>
                      {' '}
                      · {t('col.evidence')}:{' '}
                      <Link to={`/evidence?q=${encodeURIComponent(r.rule_id ?? r.field_id ?? '')}`}>
                        {r.evidence_required}
                      </Link>
                    </>
                  )}
                  {r.citation && <span className="muted"> · {r.citation}</span>}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <p className="muted small glossary">{t('glossary.not_evaluable')}</p>
      {!compact && v.cmc_basis.length > 0 && (
        <>
          <h3>{t('batch.cmc_basis')}</h3>
          <ul>
            {v.cmc_basis.map((c) => (
              <li key={c.cmc}>
                <strong>{c.cmc}</strong> <Enum ns="cmc_state" v={c.state} />
                {c.basis ? ` (${t('batch.basis')})` : ''}:{' '}
                {c.gates
                  .map((g) => `${g.rule_id} ${t(`gate_state.${g.gate_state}`, g.gate_state)}`)
                  .join(', ')}
              </li>
            ))}
          </ul>
        </>
      )}
      {!compact && v.open_gates.length > 0 && (
        <>
          <h3>{t('batch.open_gates')}</h3>
          <ul>
            {v.open_gates.map((g) => (
              <li key={g.gate}>
                <code>{g.gate}</code> {names.rule(g.gate)} <Enum ns="gate_state" v={g.state} /> ·{' '}
                {t('col.owner')}: {owner(g.owner)} · {g.closes_with}
                {g.covered_by_assumption && (
                  <>
                    {' '}
                    · {t('batch.covered')} <code>{g.covered_by_assumption}</code>
                  </>
                )}
                {g.blocks_route && !g.covered_by_assumption && <strong> · {t('batch.blocks_route')}</strong>}
              </li>
            ))}
          </ul>
        </>
      )}
      {!compact && (
        <details>
          <summary>{t('batch.rules')}</summary>
          <table>
            <thead>
              <tr>
                <th>{t('col.rule')}</th>
                <th>{t('col.result')}</th>
                <th>{t('col.layer')}</th>
                <th>{t('col.severity')}</th>
                <th>{t('col.inputs')}</th>
                <th>{t('col.basis')}</th>
              </tr>
            </thead>
            <tbody>
              {v.rule_results.map((r) => (
                <tr key={r.rule_id}>
                  <td>
                    <code>{r.rule_id}</code>
                    <div className="small muted">{names.rule(r.rule_id)}</div>
                  </td>
                  <td>
                    <ResultTag result={r.result} />
                    {r.assumed && ' *'}
                  </td>
                  <td>
                    <Enum ns="layer" v={r.layer} />
                  </td>
                  <td>
                    {r.severity_effective}
                    {r.provisional ? ` (${t('batch.unverified')})` : ''}
                  </td>
                  <td className="small">
                    {r.inputs.map((i) => `${i.field_id}=${fmtValue(i.raw_value)}`).join('; ')}
                  </td>
                  <td className="small">{r.citation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      <p className="muted small">
        {t('batch.evaluation_id')} {e.evaluation_id} · {t('batch.versions')}{' '}
        {v.versions.overlay_chain.join(' + ')} · {v.versions.route_model} · {t('batch.engine')}{' '}
        {v.versions.engine}
      </p>
    </section>
  );
}
